import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get, post, patch, del, toastError } from "@/lib/api";
import { nok, pct, num } from "@/lib/format";
import { CALC_GROUPS, label } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState } from "@/components/common/States";
import { Plus, Trash2, AlertTriangle, CheckCircle2, Lock, Loader2, FileText, Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const NumberCell = ({ value, onCommit, disabled, className, step = "any", testId }) => {
  const [v, setV] = useState(value ?? "");
  const [focus, setFocus] = useState(false);
  return (
    <Input
      data-testid={testId}
      type="number"
      step={step}
      disabled={disabled}
      className={cn("h-7 text-right tabular text-xs px-1.5", className)}
      value={focus ? v : value ?? ""}
      onFocus={() => { setV(value ?? ""); setFocus(true); }}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { setFocus(false); if (String(v) !== String(value ?? "")) onCommit(v === "" ? 0 : Number(v)); }}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
};

export const CalculationEditor = ({ calcId, onQuoteCreated }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const key = ["calculation", calcId];
  const { data: c, isLoading } = useQuery({ queryKey: key, queryFn: () => get(`/calculations/${calcId}`), enabled: !!calcId });
  const { data: rates } = useQuery({ queryKey: ["rates"], queryFn: () => get("/rates") });
  const [addOpen, setAddOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const invalidate = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["lead"] }); qc.invalidateQueries({ queryKey: ["project"] }); };

  const updLine = useMutation({ mutationFn: ({ id, ...body }) => patch(`/calculations/${calcId}/lines/${id}`, body), onSuccess: invalidate, onError: (e) => toastError(e) });
  const delLine = useMutation({ mutationFn: (id) => del(`/calculations/${calcId}/lines/${id}`), onSuccess: invalidate, onError: (e) => toastError(e) });
  const updCalc = useMutation({ mutationFn: (body) => patch(`/calculations/${calcId}`, body), onSuccess: invalidate, onError: (e) => toastError(e) });
  const approve = useMutation({ mutationFn: () => post(`/calculations/${calcId}/approve`, {}), onSuccess: () => { toast.success("Kalkyle godkjent"); invalidate(); }, onError: (e) => toastError(e) });
  const duplicate = useMutation({ mutationFn: () => post(`/calculations/${calcId}/duplicate`), onSuccess: (d) => { toast.success(`Kopi ${d.calc_number} opprettet`); invalidate(); }, onError: (e) => toastError(e) });

  if (isLoading || !c) return <LoadingState className="py-10" />;
  const locked = c.status === "laast";
  const editable = can("calculations:write") && !locked;
  const t = c.totals || {};
  const groups = CALC_GROUPS.filter((g) => c.lines.some((l) => l.group === g));

  return (
    <div className="grid xl:grid-cols-[1fr_320px] gap-4" data-testid="calculation-editor">
      <div className="space-y-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{c.calc_number}</span>
            <StatusBadge status={c.status} entity="calc" />
            {locked && <span className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> Låst – brukt i sendt tilbud</span>}
          </div>
          <div className="flex items-center gap-2">
            {editable && <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="calc-add-line-button"><Plus className="h-4 w-4 mr-1" /> Linje</Button>}
            <Button size="sm" variant="outline" onClick={() => duplicate.mutate()} data-testid="calc-duplicate-button"><Copy className="h-4 w-4 mr-1" /> Kopi</Button>
          </div>
        </div>
        <div className="rounded-md border bg-card overflow-x-auto" data-testid="calculation-lines-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Beskrivelse</th>
                <th className="text-right px-2 py-2 font-medium w-20">Antall</th>
                <th className="text-left px-2 py-2 font-medium w-14">Enhet</th>
                <th className="text-right px-2 py-2 font-medium w-16">Mnd</th>
                <th className="text-right px-2 py-2 font-medium w-24">Kost/enh</th>
                <th className="text-right px-2 py-2 font-medium w-24">Pris/enh</th>
                <th className="text-right px-2 py-2 font-medium w-16">Rab %</th>
                <th className="text-right px-2 py-2 font-medium w-28">Kost</th>
                <th className="text-right px-2 py-2 font-medium w-28">Sum</th>
                <th className="text-right px-2 py-2 font-medium w-14">DG</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {c.lines.length === 0 && <tr><td colSpan={11} className="text-center text-muted-foreground py-8">Ingen linjer. Generer fra løsningsforslag eller legg til manuelt.</td></tr>}
              {groups.map((g) => (
                <GroupRows key={g} g={g} lines={c.lines.filter((l) => l.group === g).sort((a, b) => (a.sort || 0) - (b.sort || 0))} editable={editable} updLine={updLine} delLine={delLine} totals={t} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <aside className="space-y-3">
        <div className="rounded-md border bg-card p-4 space-y-3 xl:sticky xl:top-20" data-testid="calculation-margin-panel">
          <div className="text-sm font-semibold">Margin</div>
          <Kv label="Omsetning" value={nok(t.revenue)} big />
          <Kv label="Estimert kost" value={nok(t.cost)} />
          <Kv label="Dekningsbidrag" value={nok(t.contribution)} />
          <div className={cn("rounded-md p-3", c.below_threshold ? "bg-rose-50" : "bg-emerald-50")}>
            <div className="text-[12px] text-muted-foreground">Dekningsgrad</div>
            <div className={cn("text-3xl font-semibold tabular", c.below_threshold ? "text-rose-700" : "text-emerald-700")} data-testid="calc-margin-pct">{pct(t.margin_pct)}</div>
            <div className="text-xs text-muted-foreground">Terskel {pct(c.margin_threshold_pct, 0)}</div>
          </div>
          {c.below_threshold && (
            <Alert className="border-amber-300 bg-amber-50" data-testid="calculation-margin-threshold-alert">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertTitle className="text-amber-900 text-sm">Under marginterskel</AlertTitle>
              <AlertDescription className="text-xs text-amber-900">{c.requires_approval ? "Krever godkjenning fra leder før tilbud kan opprettes." : "Varsel: margin er under konfigurert terskel."}</AlertDescription>
            </Alert>
          )}
          {c.status === "godkjent" && <div className="text-xs text-emerald-800 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Godkjent av {c.approved_by_name}</div>}
          <div className="pt-2 border-t space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Global rabatt %</Label>
              <NumberCell value={c.global_discount_pct} disabled={!editable} className="w-20" onCommit={(v) => updCalc.mutate({ global_discount_pct: v })} testId="calc-global-discount-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Varighet (mnd)</Label>
              <NumberCell value={c.duration_months} disabled={!editable} className="w-20" onCommit={(v) => updCalc.mutate({ duration_months: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Kompleksitet</Label>
              <Select value={c.complexity} disabled={!editable} onValueChange={(v) => updCalc.mutate({ complexity: v })}>
                <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{["enkel", "medium", "kompleks"].map((x) => <SelectItem key={x} value={x}>{label(x)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {t.monthly_total > 0 && <Kv label="Månedspris (leie)" value={nok(t.monthly_total)} />}
            {t.establishment_total > 0 && <Kv label="Etablering/engangs" value={nok(t.establishment_total)} />}
            {c.travel_context?.distance_km && <div className="text-xs text-muted-foreground">Avstand {num(c.travel_context.distance_km)} km · {c.travel_context.ferries || 0} ferje(r)</div>}
          </div>
          <div className="pt-2 border-t space-y-2">
            {(c.requires_approval && c.status !== "godkjent") && (can("calculations:approve") || can("users:write")) && (
              <Button className="w-full" variant="outline" size="sm" onClick={() => approve.mutate()} disabled={approve.isPending} data-testid="calc-approve-button"><CheckCircle2 className="h-4 w-4 mr-1" /> Godkjenn (leder)</Button>
            )}
            {(c.requires_approval && c.status !== "godkjent") && !(can("calculations:approve") || can("users:write")) && (
              <Button className="w-full" variant="outline" size="sm" onClick={() => approve.mutate()} disabled={approve.isPending} data-testid="calc-request-approval-button">Send til godkjenning</Button>
            )}
            {can("quotes:write") && c.lines.length > 0 && (
              <Button className="w-full" size="sm" onClick={() => setQuoteOpen(true)} disabled={c.requires_approval && c.status !== "godkjent"} data-testid="calc-create-quote-button"><FileText className="h-4 w-4 mr-1" /> Opprett tilbud</Button>
            )}
            {c.quotes?.length > 0 && (
              <div className="text-xs text-muted-foreground">Tilbud: {c.quotes.map((q) => <button key={q.id} className="font-mono underline mr-1" onClick={() => navigate(`/tilbud/${q.id}`)}>{q.quote_number}</button>)}</div>
            )}
          </div>
        </div>
      </aside>
      <AddLineSheet open={addOpen} onOpenChange={setAddOpen} rates={rates} calcId={calcId} onDone={invalidate} />
      <CreateQuoteSheet open={quoteOpen} onOpenChange={setQuoteOpen} calc={c} onCreated={(q) => { onQuoteCreated?.(q); navigate(`/tilbud/${q.id}`); }} />
    </div>
  );
};

const Kv = ({ label, value, big }) => (
  <div className="flex items-baseline justify-between gap-2">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={cn("font-mono tabular", big ? "text-lg font-semibold" : "text-sm")}>{value}</div>
  </div>
);

const GroupRows = ({ g, lines, editable, updLine, delLine, totals }) => {
  const gt = totals.groups?.[g];
  return (
    <>
      <tr className="bg-muted/30">
        <td colSpan={7} className="px-3 py-1.5 text-[12px] font-medium text-muted-foreground">{label(g)}</td>
        <td className="px-2 py-1.5 text-right text-xs font-mono text-muted-foreground">{gt ? nok(gt.cost) : ""}</td>
        <td className="px-2 py-1.5 text-right text-xs font-mono font-semibold">{gt ? nok(gt.revenue) : ""}</td>
        <td colSpan={2}></td>
      </tr>
      {lines.map((l) => {
        const dg = l.sell_total ? ((l.sell_total - l.cost_total) / l.sell_total) * 100 : 0;
        return (
          <tr key={l.id} className={cn("border-t hover:bg-muted/30", l.is_option && "opacity-70 italic")} data-testid={`calc-line-${l.id}`}>
            <td className="px-3 py-1">
              {editable ? (
                <Input className="h-7 text-sm px-1.5" defaultValue={l.description} onBlur={(e) => e.target.value !== l.description && updLine.mutate({ id: l.id, description: e.target.value })} />
              ) : (
                <span>{l.description}</span>
              )}
              {l.is_option && <span className="ml-1 text-[10px] rounded bg-muted px-1">opsjon</span>}
            </td>
            <td className="px-2 py-1"><NumberCell value={l.quantity} disabled={!editable} onCommit={(v) => updLine.mutate({ id: l.id, quantity: v })} testId={`calc-line-qty-${l.id}`} /></td>
            <td className="px-2 py-1 text-xs text-muted-foreground">{l.unit}</td>
            <td className="px-2 py-1">{l.unit === "mnd" ? <NumberCell value={l.months} disabled={!editable} onCommit={(v) => updLine.mutate({ id: l.id, months: v })} /> : <span className="text-xs text-muted-foreground text-right block">–</span>}</td>
            <td className="px-2 py-1"><NumberCell value={l.cost_unit} disabled={!editable} onCommit={(v) => updLine.mutate({ id: l.id, cost_unit: v })} /></td>
            <td className="px-2 py-1"><NumberCell value={l.sell_unit} disabled={!editable} onCommit={(v) => updLine.mutate({ id: l.id, sell_unit: v })} testId={`calc-line-sell-${l.id}`} /></td>
            <td className="px-2 py-1"><NumberCell value={l.discount_pct} disabled={!editable} onCommit={(v) => updLine.mutate({ id: l.id, discount_pct: v })} /></td>
            <td className="px-2 py-1 text-right font-mono text-xs text-muted-foreground tabular">{nok(l.cost_total)}</td>
            <td className="px-2 py-1 text-right font-mono text-xs font-medium tabular">{nok(l.sell_total)}</td>
            <td className={cn("px-2 py-1 text-right font-mono text-xs tabular", dg < 15 ? "text-rose-600" : dg < 25 ? "text-amber-600" : "text-emerald-700")}>{l.sell_total ? pct(dg, 0) : "–"}</td>
            <td className="px-1 py-1">{editable && <button className="text-muted-foreground hover:text-destructive" onClick={() => delLine.mutate(l.id)} aria-label="Slett linje" data-testid={`calc-line-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
          </tr>
        );
      })}
    </>
  );
};

const AddLineSheet = ({ open, onOpenChange, rates, calcId, onDone }) => {
  const [f, setF] = useState({ group: "annet", rate_id: "", description: "", quantity: 1, unit: "stk", cost_unit: 0, sell_unit: 0, months: "", is_option: false });
  const add = useMutation({
    mutationFn: () => post(`/calculations/${calcId}/lines`, { ...f, rate_id: f.rate_id || null, months: f.months ? Number(f.months) : null, quantity: Number(f.quantity), cost_unit: Number(f.cost_unit), sell_unit: Number(f.sell_unit) }),
    onSuccess: () => { onDone(); onOpenChange(false); setF({ group: "annet", rate_id: "", description: "", quantity: 1, unit: "stk", cost_unit: 0, sell_unit: 0, months: "", is_option: false }); },
    onError: (e) => toastError(e),
  });
  const pickRate = (id) => {
    const r = (rates || []).find((x) => x.id === id);
    if (r) setF({ ...f, rate_id: id, description: r.name, unit: r.unit, cost_unit: r.cost_price, sell_unit: r.sell_price, group: r.category === "prosjektering" ? "prosjektering" : r.category === "personell" ? "personell" : r.category === "reise" ? "reise" : r.category === "transport" ? "transport" : r.category === "montasje" ? "montasje" : r.category === "eksternt" ? "eksternt" : "annet" });
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[460px] max-w-[94vw] overflow-y-auto">
        <SheetHeader><SheetTitle>Legg til kalkylelinje</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-3">
          <div>
            <Label>Fra sats (valgfritt)</Label>
            <Select value={f.rate_id || "none"} onValueChange={(v) => v === "none" ? setF({ ...f, rate_id: "" }) : pickRate(v)}>
              <SelectTrigger data-testid="calc-add-rate-select"><SelectValue placeholder="Velg sats" /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="none">Manuell linje</SelectItem>{(rates || []).filter((r) => r.active).map((r) => <SelectItem key={r.id} value={r.id}>{label(r.category)} – {r.name} ({nok(r.sell_price)}/{r.unit})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Beskrivelse *</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="calc-add-description-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Gruppe</Label>
              <Select value={f.group} onValueChange={(v) => setF({ ...f, group: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CALC_GROUPS.map((g) => <SelectItem key={g} value={g}>{label(g)}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Enhet</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></div>
            <div><Label>Antall</Label><Input type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="calc-add-quantity-input" /></div>
            <div><Label>Måneder (for leie)</Label><Input type="number" value={f.months} onChange={(e) => setF({ ...f, months: e.target.value })} /></div>
            <div><Label>Kost per enhet</Label><Input type="number" value={f.cost_unit} onChange={(e) => setF({ ...f, cost_unit: e.target.value })} /></div>
            <div><Label>Pris per enhet</Label><Input type="number" value={f.sell_unit} onChange={(e) => setF({ ...f, sell_unit: e.target.value })} data-testid="calc-add-sell-input" /></div>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3"><Label>Opsjon (ikke i totalsum)</Label><Switch checked={f.is_option} onCheckedChange={(v) => setF({ ...f, is_option: v })} /></div>
          <Button className="w-full" disabled={!f.description || add.isPending} onClick={() => add.mutate()} data-testid="calc-add-submit-button">Legg til</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const CreateQuoteSheet = ({ open, onOpenChange, calc, onCreated }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: calc?.name?.replace(/^Kalkyle\s*/i, "Tilbud ") || "", valid_days: 30 });
  const create = useMutation({
    mutationFn: () => post("/quotes", { customer_id: calc.customer_id, lead_id: calc.lead_id, project_id: calc.project_id, calculation_id: calc.id, title: f.title, valid_days: Number(f.valid_days) }),
    onSuccess: (q) => { toast.success(`Tilbud ${q.quote_number} opprettet`); qc.invalidateQueries({ queryKey: ["quotes"] }); qc.invalidateQueries({ queryKey: ["lead"] }); onOpenChange(false); onCreated(q); },
    onError: (e) => toastError(e),
  });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[440px] max-w-[94vw]">
        <SheetHeader><SheetTitle>Opprett tilbud fra kalkyle</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-3">
          <div className="rounded-md bg-muted/50 p-3 text-sm"><div className="text-xs text-muted-foreground">Kalkyle</div><div>{calc?.calc_number} – {calc?.name}</div><div className="font-mono text-xs mt-1">{nok(calc?.totals?.revenue)} eks. mva · DG {pct(calc?.totals?.margin_pct)}</div></div>
          <div><Label>Tittel på tilbud *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="quote-title-input" /></div>
          <div><Label>Gyldig antall dager</Label><Input type="number" value={f.valid_days} onChange={(e) => setF({ ...f, valid_days: e.target.value })} /></div>
          <Button className="w-full" disabled={!f.title || create.isPending} onClick={() => create.mutate()} data-testid="quote-create-submit-button">{create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Opprett tilbud v1</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
